import { useQuery } from '@tanstack/react-query';
import { AuthRequiredError, unwrap } from '@truecare/api-client';
import { api } from '../api';

/**
 * Fetches `/api/v1/auth/me` and exposes the current user (or null if
 * signed-out). 401s resolve as `{ data: null }` rather than throwing —
 * the sign-in pages need to render normally for signed-out users.
 *
 * For routes that REQUIRE a signed-in user (e.g. `/me`), use the
 * router's beforeLoad to redirect to /sign-in on a 401; this hook is
 * for components that just want "who's signed in right now" without
 * forcing a redirect.
 */
export function useCurrentUser() {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      try {
        return await unwrap(api.GET('/api/v1/auth/me'));
      } catch (err) {
        if (err instanceof AuthRequiredError) return null;
        throw err;
      }
    },
    // 30-second stale time keeps repeated component mounts (e.g. layout
    // switches) from re-hitting /me; longer than that risks a stale
    // logged-out cookie still appearing logged-in.
    staleTime: 30_000,
    retry: false,
  });
}

export type CurrentUser = Awaited<ReturnType<typeof useCurrentUser>>['data'];
